/**
 * Default export pour l'auto-discovery `apps/web/src/routes/modules.ts`.
 *
 * Onglet « Fiches neuves » (icône lucide `clipboard-check`, spec
 * `SPEC-lots-A-B.md` §1.1 et §1.4), visible à TOUT compte connecté (absent de
 * `apps/web/src/lib/acces.ts` `ECRANS_RESERVES_ADMIN`) : lecture seule de la
 * boutique ; « Écart accepté » = admin ou compte 7, refusé côté API pour les autres.
 */
import { lazy, Suspense } from 'react';
import type { ModuleRoute } from '@herbo/module-route';

const FichesNeuves = lazy(() => import('./pages/FichesNeuves'));
const NouveauProduit = lazy(() => import('./creation/pages/NouveauProduit'));
const ReglagesCatalogue = lazy(() => import('./creation/reglages/ReglagesCatalogue'));

const route: ModuleRoute = {
  path: 'fiches-neuves',
  label: 'Fiches neuves',
  icon: 'clipboard-check',
  apres: 'changements-de-prix',
  element: (
    <Suspense fallback={<div className="px-6 py-8 text-sm text-slate-500">Chargement des fiches neuves…</div>}>
      <FichesNeuves />
    </Suspense>
  ),
};

/**
 * Lot C « Nouveau produit » (spec `SPEC-lot-C.md` §3) : deux routes sans onglet
 * (`hidden`), atteintes depuis « Fiches neuves » (bouton « Nouveau produit »,
 * onglet « Brouillons ») ; l'onglet « Fiches neuves » reste allumé (même
 * premier segment). Écriture réservée à admin ou compte 7, côté API.
 */
const creation = (path: string): ModuleRoute => ({
  path,
  label: 'Nouveau produit',
  icon: 'file-plus-2',
  hidden: true,
  // Rangées derrière « Fiches neuves » : la coquille retrouve l'onglet actif par le premier segment, la première route trouvée doit être la sienne.
  apres: 'fiches-neuves',
  element: (
    <Suspense fallback={<div className="px-6 py-8 text-sm text-slate-500">Chargement de la création…</div>}>
      <NouveauProduit />
    </Suspense>
  ),
});

/**
 * Réglages › Catalogue (skills de rédaction, lexique d'allégations ; Ben 30/10) :
 * sous `fiches-neuves` et non sous `admin`, car l'onglet Réglages est réservé
 * aux admins alors que Vir (compte 7) écrit ces réglages. La carte « Catalogue »
 * de la page Réglages y mène.
 */
const reglages: ModuleRoute = {
  path: 'fiches-neuves/reglages',
  label: 'Réglages du catalogue',
  icon: 'settings',
  hidden: true,
  apres: 'fiches-neuves',
  element: (
    <Suspense fallback={<div className="px-6 py-8 text-sm text-slate-500">Chargement des réglages du catalogue…</div>}>
      <ReglagesCatalogue />
    </Suspense>
  ),
};

export default [route, creation('fiches-neuves/nouveau'), creation('fiches-neuves/brouillon/:id'), reglages];
